<script lang="ts">
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { groups } from "@frontend/grouplist";
    import { RefreshGroupLimits } from "@frontend/grouplimitlist";
    import { translate } from "../Translator";
    import type { AuthenticatedRequestOfEditGroupLimitDTO, EditGroupLimitDTO, Money, User, GroupLimitResponse } from "@frontend/walletapi";
    import { usersByRole, RefreshUsers } from "@frontend/userlist";
    import { onMount } from "svelte";
    import { get } from "svelte/store";

    export let dismiss: () => void;
    export let groupLimit: GroupLimitResponse;

    let groupId: number | undefined = undefined;
    let userId: number | undefined = undefined;
    let initialBalance: number = 0;
    let balance: number = 0;
    let isActive: boolean = true;
    let errorMessage = '';
    let successMessage = '';
    let isSubmitting = false;
    
    // Lista de todos los usuarios
    let allUsers: User[] = [];
    
    // Función para obtener todos los usuarios de todos los roles
    function getAllUsers(): User[] {
        const allUsersList: User[] = [];
        Object.values(usersByRole).forEach(userStore => {
            const users = get(userStore);
            allUsersList.push(...users);
        });
        return allUsersList.sort((a, b) => (a.FirstName || '').localeCompare(b.FirstName || ''));
    }
    
    // Función para obtener el nombre completo del usuario
    function getUserDisplayName(user: User): string {
        const firstName = user.FirstName || '';
        const lastName = user.LastName || '';
        const email = user.Email || '';
        return `${firstName} ${lastName}`.trim() || email || `ID: ${user.Id}`;
    }

    // Función para crear objeto Money
    function createMoney(amount: number): Money {
        return {
            InCents: Math.round(amount * 100)
        };
    }

    // Función para convertir Money a número
    function moneyToNumber(money: Money | undefined): number {
        if (!money) return 0;
        return typeof money === 'object' ? money.InCents / 100 : Number(money);
    }

    // Inicializar los valores del formulario con los datos del groupLimit
    function initializeForm() {
        if (!groupLimit) {
            console.error('groupLimit es undefined en initializeForm');
            return;
        }

        
        groupId = groupLimit.GroupId;
        userId = groupLimit.UserId;
        initialBalance = moneyToNumber(groupLimit.InitialBalance);
        balance = moneyToNumber(groupLimit.Balance);
        isActive = groupLimit.Active ?? true;
        
      
    }

    async function submitForm() {
        if (!groupLimit || !groupLimit.Id) {
            errorMessage = 'Error';
            return;
        }

        if (!groupId) {
            errorMessage = 'Please select a group.';
            return;
        }

        if (initialBalance < 0) {
            errorMessage = 'The opening balance cannot be negative.';
            return;
        }

        if (balance < 0) {
            errorMessage = 'El balance no puede ser negativo..';
            return;
        }

        isSubmitting = true;
        errorMessage = '';

        try {
            const requestPayload: AuthenticatedRequestOfEditGroupLimitDTO = {
                Token: GetCachedToken(),
                Data: {
                    Id: groupLimit.Id,
                    GroupId: groupId,
                    UserId: userId || undefined,
                    InitialBalance: createMoney(initialBalance),
                    Balance: createMoney(balance),
                    DateTransaction: new Date(),
                    Active: isActive
                }
            };

            const result = await service.walletapi.editGroupLimit(requestPayload);

            if (result.Success) {
                successMessage = result.Message || 'Group limit successfully updated.';
                setTimeout(() => {
                    successMessage = '';
                    dismiss();
                }, 2000);
                await RefreshGroupLimits();
            } else {
                errorMessage = result.ErrorMessage || 'Error updating group limit.';
            }
        } catch (error) {
            errorMessage = 'Connection error. Please try again..';
        } finally {
            isSubmitting = false;
        }
    }

    function handleCancel() {
        dismiss();
    }
    
    onMount(async () => {
        await RefreshUsers();
        allUsers = getAllUsers();
        initializeForm();
    });
    
    $: {
        allUsers = getAllUsers();
    }
</script>

<style>
    .form-container {
        padding: 1em;
    }
    .field-group {
        margin-bottom: 1em;
    }
</style>

<div class="form-container">
    <h2 class="ui header">Edit Group Limit</h2>

    {#if errorMessage}
        <div class="ui negative message">
            <div class="header">Error</div>
            <p>{errorMessage}</p>
        </div>
    {/if}

    {#if successMessage}
        <div class="ui positive message">
            <div class="header">Success</div>
            <p>{successMessage}</p>
        </div>
    {/if}

    <form class="ui form" on:submit|preventDefault={submitForm}>
        <div class="field-group">
            <label for="groupId">Group *</label>
            <select id="groupId" class="ui dropdown" bind:value={groupId} required>
                <option value={undefined}>Select group...</option>
                {#each $groups as group}
                    <option value={group.Id}>{group.Name} (ID: {group.Id})</option>
                {/each}
            </select>
        </div>

        <div class="field-group">
            <label for="userId">User (Optional)</label>
            <select id="userId" class="ui dropdown" bind:value={userId}>
                <option value={undefined}>Select user (opcional)...</option>
                {#each allUsers as user}
                    <option value={user.Id}>
                        {getUserDisplayName(user)} - {user.Role} (ID: {user.Id})
                        {#if user.Id === $me?.Id} ← Tú{/if}
                    </option>
                {/each}
            </select>
        </div>

        <div class="field-group">
            <label for="initialBalance">Initial Balance *</label>
            <div class="ui labeled input">
                <div class="ui label">$</div>
                <input 
                    id="initialBalance" 
                    type="number" 
                    placeholder="0.00"
                    bind:value={initialBalance}
                    step="0.01"
                    min="0"
                    required
                />
            </div>
        </div>

        <div class="field-group">
            <label for="balance">Current Balance *</label>
            <div class="ui labeled input">
                <div class="ui label">$</div>
                <input 
                    id="balance" 
                    type="number" 
                    placeholder="0.00"
                    bind:value={balance}
                    step="0.01"
                    min="0"
                    required
                />
            </div>
        </div>

        <div class="field-group">
            <label for="isActive">Status</label>
            <div class="ui toggle checkbox">
                <input 
                    id="isActive" 
                    type="checkbox" 
                    bind:checked={isActive}
                />
                <label for="isActive">
                    {#if isActive}
                        <span class="ui green text">Active</span>
                    {:else}
                        <span class="ui red text">Inactive</span>
                    {/if}
                </label>
            </div>
            <small class="ui grey text">Determines whether the group limit is active or inactive.</small>
        </div>

        <div class="ui divider"></div>

        <div class="ui buttons">
            <button type="button" class="ui button" on:click={handleCancel}>
                Cancel
            </button>
            <div class="or"></div>
            <button type="submit" class="ui positive button" disabled={isSubmitting}>
                {#if isSubmitting}
                    <i class="spinner loading icon"></i>
                    Updating...
                {:else}
                    <i class="save icon"></i>
                   Update Limit
                {/if}
            </button>
        </div>
    </form>
</div>